import { UntypedFormGroup } from '@angular/forms';

import { Guest } from './models';
import { Observable } from 'rxjs';
import jwt_decode from 'jwt-decode';
import * as CryptoJS from 'crypto-js';
import { constants } from './constants/constants';
/**
 * This function coerces a string into a string literal type.
 * Using tagged union types in TypeScript 2.0, this enables
 * powerful typechecking of our reducers.
 *
 * Since every action label passes through this function it
 * is a good place to ensure all of our action labels
 * are unique.
 */

const labelCache: { [label: string]: boolean } = {};
export function label<T>(labl: T | ''): T {
  if (labelCache[<string>labl]) {
    throw new Error(`Action type '${labl}' is not unique'`);
  }

  labelCache[<string>labl] = true;

  return <T>labl;
}

export type SelectorFn<T, V> = (input$: Observable<T>) => Observable<V>;

export interface Selector<T, V> extends SelectorFn<T, V> {
  readonly cachedResult?: null | Observable<V>;
  reset(): void;
  override(source$: Observable<V>): void;
}

export function toArray(input: any): any {
  return Object.keys(input).map(function (key) {
    return { type: key, name: input[key] };
  });
}

export function writeLocalStorage(key: string, value: any) {
  if (value) {
    value = JSON.stringify(value);
  }
  localStorage.setItem(key, value);
}

export function readLocalSorage<T>(key: string): any {
  const value = localStorage.getItem(key);

  if (value && value !== 'undefined' && value !== 'null') {
    return <T>JSON.parse(value);
  }

  return null;
}

export function onValueChanged(
  form: UntypedFormGroup,
  formErrors: any,
  validationMessages: any,
  formSubmitted: boolean
) {
  let titlebarError;
  if (form === null) {
    return;
  }
  let showTitleBarError = false;

  for (const field of formErrors) {
    // clear previous error message (if any)
    formErrors[field] = '';
    const control = form.get(field);

    const isDirtyOrBlank = !control?.valid && formSubmitted;

    if (control && isDirtyOrBlank) {
      const messages = validationMessages[field];
      const errors = control?.errors || {};
      for (const key of Object.keys(errors)) {
        showTitleBarError = true;
        formErrors[field] = messages[key];
      }
    }
  }
  if (showTitleBarError) {
    titlebarError = validationMessages['genericError'];
  }

  return {
    formErrors: formErrors,
    titlebarError: titlebarError,
    showTitleBarError: showTitleBarError,
  };
}

export function getGuestFullName(guest: Guest): string {
  return guest.middleName
    ? `${guest.firstName} ${guest.middleName} ${getGuestLastName(guest)}`
    : `${guest.firstName} ${getGuestLastName(guest)}`;
}

export function getGuestFLName(guest: Guest): string {
  if (!guest) return '';
  return `${guest.firstName} ${getGuestLastName(guest)}`;
}

export function capitalizeSuffix(string: string) {
  if (string.length === 0) return '';
  if (string === 'JR' || string === 'SR') {
    string = string.toLowerCase();
    return string.charAt(0).toUpperCase() + string.slice(1);
  } else {
    return string;
  }
}

export function capitalizeNameAllWords(inputValue: string): string {
  if (!inputValue) {
    return '';
  }

  const lower = inputValue.toLowerCase().split(' ');
  let outputValue = '';
  for (let i = 0; i < lower.length; i++) {
    outputValue += capitalizeName(lower[i]);
    outputValue += i < lower.length - 1 ? ' ' : '';
  }
  return outputValue + '';
}

export function capitalizeName(string: string) {
  const name = string.toLowerCase();
  return name.charAt(0).toUpperCase() + name.slice(1);
}

export function allCapsSuffix(suffix: any) {
  if (!suffix) return '';

  const numberSuffixes = [' Ii', ' Iii', ' Iv', ' Vi'];
  const lastNameHasSuffix = numberSuffixes
    .map((item) => suffix.indexOf(item, -4))
    .filter((item) => item.valueOf() !== -1);
  if (lastNameHasSuffix.length > 0) {
    const nameWords = suffix.split(' ');
    const upperCaseSuffix = nameWords.pop().toUpperCase();
    const newNameWords = nameWords.join(' ');
    return newNameWords + ' ' + upperCaseSuffix;
  } else {
    return suffix;
  }
}

// sometimes the lastName has a suffix appended to the lastName (in Firstlettercaps) so we will remove it.
export function getGuestLastName(guest: any) {
  const suffixes = [' Jr', ' Sr', ' I', ' Ii', ' Iii', ' Iv', ' V', ' Vi'];
  const suffix = suffixes.find((item) => guest.lastName.endsWith(item));

  if (suffix) {
    return guest.lastName.slice(0, guest.lastName.length - suffix.length);
  }

  return guest.lastName;
}

/**
 * This is a table containing special UTF8 characters with uppercase <-> lowercase
 * interpretation. Those characters are special cases not managed in an standard
 * way from string.toUpperCase or string.toLowerCase. See replacement methods
 * <code>extendedToLowerCase</code> and <code>extendedToUpperCase</code>.
 * This table was done to support special error cases on user story OCLI-4969.
 * Following characters are causing backend to break with an error of type
 * "Could not parse XML; nested exception is org.xml.sax.SAXParseException;
 * Character reference"
 */
const nonStandardCharacterReplacements = [
  // Latin letters with ogonek (currently getting lost on backend)
  { specialCharacter: 'ą', standardReplacement: 'a' },
  { specialCharacter: 'ę', standardReplacement: 'e' },
  { specialCharacter: 'į', standardReplacement: 'i' },
  { specialCharacter: 'ǫ', standardReplacement: 'o' },
  { specialCharacter: 'ų', standardReplacement: 'u' },
  { specialCharacter: 'Ą', standardReplacement: 'a' },
  { specialCharacter: 'Ę', standardReplacement: 'e' },
  { specialCharacter: 'Į', standardReplacement: 'i' },
  { specialCharacter: 'Ǫ', standardReplacement: 'o' },
  { specialCharacter: 'Ų', standardReplacement: 'u' },
];

/**
 * This function returns a modified copy of the given input string removing or
 * changing some UTF8 characters that can not be handled (yet) by the backend.
 * This function was done to support special error cases on user story OCLI-4969.
 * @param inputString
 */
export function replaceNonSupportedCharacters(inputString: string): string {
  if (!inputString) {
    return '';
  }
  let outputString = inputString;
  nonStandardCharacterReplacements.forEach((element) => {
    outputString = outputString.replace(
      element.specialCharacter,
      element.standardReplacement
    );
  });
  return outputString;
}

/**
 * Variant of string.toLowerCase supporting special non-standard UTF8 character mappings.
 * This function was done to support special error cases on user story OCLI-4969.
 * @param inputString input string.
 * @returns lowercase string with special UTF8 mappings.
 */
function extendedToLowerCase(inputString: string): string {
  const intermediateString = replaceNonSupportedCharacters(inputString);
  return intermediateString.toLowerCase();
}

/**
 * Variant of string.toLowerCase supporting special non-standard UTF8 character mappings.
 * This function was done to support special error cases on user story OCLI-4969.
 * @param inputString input string.
 * @returns uppercase string with special UTF8 mappings.
 */
function extendedToUpperCase(inputString: string): string {
  const intermediateString = replaceNonSupportedCharacters(inputString);
  return intermediateString.toUpperCase();
}

/**
 * This function takes a unicode UTF8 encoded string and returns a
 * "capitalized version" in which the first character of each word is capitalized
 * and all the remaining characters in each word are lowercase.
 *
 * Note that for english names using only 7 bit ASCII characters, a regular expression
 * would work:
 *     return name.toLowerCase().replace(/\b(\w)/g, s => s.toUpperCase());
 * but the base problem with this regular expression is the \w character inclussion rule
 * which takes the set [A-Za-z0-9_] as letters and treats UTF8 extended characters as
 * whitespace, leading to wrong application of toLowerCase / toUpperCase conversions.
 * Current implementation treats special cases of separators and builds the
 * needed name word by word and letter by letter.
 * @param name input original name to be converted
 */
export function capitalizeString(name: string) {
  // 1. Border case: not valid name is null
  if (!name) {
    return '';
  }

  // 2. All letters in the name are initially set to lowercase
  const lowerCase = extendedToLowerCase(name);
  const normalizedWhiteSpaces = lowerCase.replace('\n', ' ').replace('\t', ' ');
  const wordSet = normalizedWhiteSpaces.split(' ');

  // 3. Accumulate letters to final name
  let concat = '';
  for (let i = 0; i < wordSet.length; i++) {
    const word = wordSet[i];

    // 3.1. For every word in the given name...
    for (let j = 0; j < word.length; j++) {
      if (j === 0) {
        // 3.1.1. First letter of every words is uppercase
        concat += extendedToUpperCase(word.charAt(j));
      } else {
        // 3.1.2. Every trailing letter is lowercase
        concat += word.charAt(j);
      }
    }

    // 3.2. Each non final word adds a white space
    if (i < wordSet.length - 1) {
      concat += ' ';
    }
  }

  // 4. Return accumulated letter set as new name
  return concat;
}

export function encryptData(data: string): string {
  const encryptionKey = readCookie(constants.COOKIES.OLCI.CPE);
  if (encryptionKey !== null) {
    return CryptoJS.AES.encrypt(data, encryptionKey).toString();
  }
  return '';
}

export function getDecodedAccessToken(token: string): any {
  try {
    const cookie = readCookie(token);
    if (!cookie) return null;
    return jwt_decode(cookie);
  } catch (err) {
    console.error('Failed to decode access token', err);
    return null;
  }
}

export function readCookie(name: any): any {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (const i of ca) {
    let c = i;
    while (c.charAt(0) === ' ') {
      c = c.substring(1, c.length);
    }
    if (c.indexOf(nameEQ) === 0) {
      return c.substring(nameEQ.length, c.length);
    }
  }
  return null;
}
export function isSafari() {
  return (
    !!navigator.userAgent.match(/Safari/) &&
    !navigator.userAgent.match(/CriOS/) &&
    !navigator.userAgent.match(/Chrome/)
  );
}

export function isIeOrEdge() {
  return (
    (navigator.userAgent.match(/.NET/) &&
      navigator.userAgent.match(/Trident/)) ||
    navigator.userAgent.match(/Edge/)
  );
}

export function addUnderscore(text: string): string {
  return text.split(' ').join('_');
}

export function removeUnderscore(text: string): string {
  return text.split('_').join(' ');
}

export function getCountryCode(): string | null {
  const cookieWucNonProd = readCookie(constants.COOKIES.GA.WUC_NONPROD) ?? '';
  const cookieWuc = readCookie(constants.COOKIES.GA.WUC) ?? '';
  
  if (cookieWucNonProd && cookieWucNonProd !== '') {
    return cookieWucNonProd;
  }
  
  if (cookieWuc && cookieWuc !== '') {
    return cookieWuc;
  }

  const guestsObj = window.sessionStorage.getItem(constants.SESSION_STORAGE_KEYS.GUESTS);
  if (guestsObj) {
    try {
       const parsedGuests: unknown = JSON.parse(guestsObj);
       if (Array.isArray(parsedGuests) && parsedGuests.length > 0) {
         const guest = parsedGuests[0] as Partial<Guest> | null;
         if (guest?.countryCode) {
           return guest.countryCode;
         }
       }
     } catch {
       // Ignore malformed session storage data and continue with other fallbacks.
     }
  }
  
  // Decode OLCI cookie directly without needing GatingServices
  const olciDataCookie: any = getDecodedAccessToken(constants.COOKIES.OLCI.OLCI_DATA);
  if (olciDataCookie?.countryOfResidency) {
    return olciDataCookie.countryOfResidency;
  }
  
  return null;
}

export function dateFormat(): string {
  const wuc = getCountryCode();
  const dayFirstCountries = constants.DATE_FORMAT.DAY_FIRST;

  return dayFirstCountries.find((c) => c === wuc)
    ? constants.DATE_FORMAT.INTERNATIONAL
    : constants.DATE_FORMAT.USA;
}

export const returnEnvironment = (env: string): string => {
  const { TEST, STAGE, PROD_ALTERNATE } = constants.ENVIRONMENTS_VALUES;
  if (env.includes(TEST)) {
    return TEST;
  }
  if (env.includes(STAGE)) {
    return STAGE;
  }
  return PROD_ALTERNATE;
}

// TODO: Refactor this function: Redundant if-else statements
export function displayBadgeOnProfile(event: boolean, element: string) {
  const menuButton = document.getElementById(
    element
  );
  if (!menuButton) return;
  const internalButton = menuButton.querySelector('button');
  if (!internalButton) return;
  internalButton.style.position = 'relative';
  const existingBadge = internalButton.querySelector('.profile-badge');
  if (event) {
    if (!existingBadge) {
      const badge = document.createElement('span');
      badge.className = 'profile-badge';
      internalButton.appendChild(badge);
    }
  } else {
    if (existingBadge) {
      existingBadge.remove();
    }
  }
}
